/**
 * BookingBadgeSubscriber — pushes pendingApprovals + overdue counts
 * via SignalR.
 *
 * Phase 5 of the badge migration. Connects to the new `bookings`
 * dashboard hub (CcmsHubAuthorize gates Admin / BookingAdmin). Deltas
 * come from BookingCreated/Approved/Rejected consumers in the
 * Dashboards service.
 *
 * Caveat: the `overdue` counter has no event-driven consumer yet —
 * BookingOverdueEvent exists in the contracts library but the
 * Dashboards consumer for it hasn't been wired. Until then, overdue is
 * maintained by the REST baseline on mount / reconnect.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useBookingService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useBookingBadgesStore } from './bookingBadgesStore';

const BOOKING_ROLES = ['BookingAdmin', 'Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[BookingBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface BookingDeltas {
  pendingApprovals?: number;
  overdue?: number;
}

export function BookingBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(BOOKING_ROLES);

  const service = useBookingService();
  const setFromMetrics = useBookingBadgesStore((s) => s.setFromMetrics);
  const reset = useBookingBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const [pending, overdue] = await Promise.all([
        service.getPending() as Promise<any[]>,
        service.getOverdue() as Promise<any[]>,
      ]);
      const pendingApprovals = (pending ?? []).length;
      const overdueCount = (overdue ?? []).length;
      log('baseline', { pendingApprovals, overdue: overdueCount });
      setFromMetrics({ pendingApprovals, overdue: overdueCount });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [service, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  // Apply deltas in-place. The booking store doesn't expose applyDelta
  // (per Phase 4 consistency it could, but the booking domain has only
  // a single counter the WS path is wired for); reduce inline so we
  // don't need to change the store shape.
  const applyDelta = useCallback(
    (m: BookingDeltas) => {
      const state = useBookingBadgesStore.getState();
      const next = {
        pendingApprovals: Math.max(0, state.pendingApprovals + (m.pendingApprovals ?? 0)),
        overdue: Math.max(0, state.overdue + (m.overdue ?? 0)),
      };
      setFromMetrics(next);
    },
    [setFromMetrics],
  );

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('bookings', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<BookingDeltas>) => {
      if (msg.domain !== 'Bookings') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<BookingDeltas>) => {
      if (msg.domain !== 'bookings') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
